// Logbook - Main logbook view with all user notes

import React, { useState, useEffect, useMemo } from 'react';
import { Book, MapPin, Calendar, Search, Filter, RefreshCw, List, Clock, CalendarDays, Plus } from 'lucide-react';
import { NoteCard } from '../notes';
import { NoteEditor } from '../notes';
import { PlaceSelector } from '../notes';
import { LogbookTimeline } from './LogbookTimeline';
import { LogbookCalendar } from './LogbookCalendar';
import { LogbookMemories } from './LogbookMemories';
import { MoodTrends } from './MoodTrends';
import {
  getUserNotes,
  deletePlaceNote,
  updatePlaceNote,
  addPlaceNote,
  getUniquePlacesCount,
  getMemoriesForToday,
} from '../../services/notesService';
import type { PlaceNote, Place, MainCategory } from '../../types';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';

interface LogbookProps {
  userId: string;
  places?: Place[];
  onNavigateToPlace?: (placeId: string) => void;
  onShowNotification?: (message: string, type: 'success' | 'error' | 'info') => void;
}

interface SelectedPlace {
  id: string;
  name: string;
  category?: string;
}

type SortOption = 'newest' | 'oldest' | 'rating';
type ViewMode = 'list' | 'timeline' | 'calendar';

export function Logbook({ userId, places = [], onNavigateToPlace, onShowNotification }: LogbookProps) {
  const [notes, setNotes] = useState<PlaceNote[]>([]);
  const [filteredNotes, setFilteredNotes] = useState<PlaceNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingNote, setEditingNote] = useState<PlaceNote | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [stats, setStats] = useState({ totalNotes: 0, uniquePlaces: 0 });
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // New entry creation flow
  const [showPlaceSelector, setShowPlaceSelector] = useState(false);
  const [creatingForPlace, setCreatingForPlace] = useState<SelectedPlace | null>(null);

  // Memories for "On this day"
  const memories = useMemo(() => getMemoriesForToday(notes), [notes]);
  const hasMemories = memories.length > 0;

  // Load notes
  const loadNotes = async () => {
    setLoading(true);
    try {
      const data = await getUserNotes(userId);
      setNotes(data);

      const uniquePlaces = await getUniquePlacesCount(userId);
      setStats({
        totalNotes: data.length,
        uniquePlaces,
      });
    } catch (error) {
      console.error('Error loading notes:', error);
      onShowNotification?.('Fehler beim Laden der Notizen', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
  }, [userId]);

  // Filter and sort notes
  useEffect(() => {
    let result = [...notes];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (note) =>
          note.placeName.toLowerCase().includes(query) ||
          note.text.toLowerCase().includes(query)
      );
    }

    // Date filter (for calendar view)
    if (selectedDate) {
      result = result.filter((note) => {
        const noteDate = note.visitDate instanceof Date
          ? note.visitDate
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          : (note.visitDate as any)?.toDate?.() ||
            (note.createdAt instanceof Date ? note.createdAt : (note.createdAt as any)?.toDate?.()) ||
            new Date(0);
        return (
          noteDate.getFullYear() === selectedDate.getFullYear() &&
          noteDate.getMonth() === selectedDate.getMonth() &&
          noteDate.getDate() === selectedDate.getDate()
        );
      });
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'newest') {
        const aDate = a.createdAt instanceof Date ? a.createdAt : (a.createdAt as any)?.toDate?.() || new Date(0);
        const bDate = b.createdAt instanceof Date ? b.createdAt : (b.createdAt as any)?.toDate?.() || new Date(0);
        return bDate.getTime() - aDate.getTime();
      } else if (sortBy === 'oldest') {
        const aDate = a.createdAt instanceof Date ? a.createdAt : (a.createdAt as any)?.toDate?.() || new Date(0);
        const bDate = b.createdAt instanceof Date ? b.createdAt : (b.createdAt as any)?.toDate?.() || new Date(0);
        return aDate.getTime() - bDate.getTime();
      } else if (sortBy === 'rating') {
        return (b.personalRating || 0) - (a.personalRating || 0);
      }
      return 0;
    });

    setFilteredNotes(result);
  }, [notes, searchQuery, sortBy, selectedDate]);

  const handleDelete = async (noteId: string) => {
    if (!window.confirm('Möchtest du diese Notiz wirklich löschen?')) {
      return;
    }

    try {
      await deletePlaceNote(noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      onShowNotification?.('Notiz gelöscht', 'success');
    } catch (error) {
      console.error('Error deleting note:', error);
      onShowNotification?.('Fehler beim Löschen', 'error');
    }
  };

  const handleSaveEdit = async (updates: {
    text: string;
    personalRating?: number;
    visitDate?: Date;
    mood?: string;
    moods?: string[];
    photoMetadata?: import('../../types').PhotoMetadata[];
    isPublic?: boolean;
  }) => {
    if (!editingNote) return;

    setIsSubmitting(true);
    try {
      await updatePlaceNote(editingNote.id, updates);

      setNotes((prev) =>
        prev.map((n) =>
          n.id === editingNote.id
            ? {
                ...n,
                ...updates,
                updatedAt: new Date(),
              }
            : n
        )
      );

      setEditingNote(null);
      onShowNotification?.('Notiz aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating note:', error);
      onShowNotification?.('Fehler beim Speichern', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle place selection for new entry
  const handlePlaceSelect = (place: SelectedPlace) => {
    setShowPlaceSelector(false);
    setCreatingForPlace(place);
  };

  // Handle saving new entry
  const handleSaveNewEntry = async (noteData: {
    text: string;
    personalRating?: number;
    visitDate?: Date;
    mood?: string;
    moods?: string[];
    photoMetadata?: import('../../types').PhotoMetadata[];
    isPublic?: boolean;
  }) => {
    if (!creatingForPlace) return;

    setIsSubmitting(true);
    try {
      const newNoteId = await addPlaceNote({
        placeId: creatingForPlace.id,
        placeName: creatingForPlace.name,
        placeCategory: creatingForPlace.category as MainCategory | undefined,
        userId,
        text: noteData.text,
        personalRating: noteData.personalRating,
        visitDate: noteData.visitDate,
        mood: noteData.mood,
        moods: noteData.moods,
        photoMetadata: noteData.photoMetadata,
        isPublic: noteData.isPublic,
      });

      // Add to local state
      const newNote: PlaceNote = {
        id: newNoteId,
        placeId: creatingForPlace.id,
        placeName: creatingForPlace.name,
        placeCategory: creatingForPlace.category as MainCategory | undefined,
        userId,
        text: noteData.text,
        personalRating: noteData.personalRating,
        visitDate: noteData.visitDate,
        mood: noteData.mood,
        moods: noteData.moods,
        photoMetadata: noteData.photoMetadata,
        isPublic: noteData.isPublic,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      setNotes((prev) => [newNote, ...prev]);
      setStats((prev) => ({
        ...prev,
        totalNotes: prev.totalNotes + 1,
      }));

      setCreatingForPlace(null);
      onShowNotification?.('Eintrag erstellt', 'success');
    } catch (error) {
      console.error('Error creating note:', error);
      onShowNotification?.('Fehler beim Erstellen', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="py-12" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-indigo-500/10 rounded-xl">
            <Book className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Mein GoodPlace Logbook</h2>
            <p className="text-sm text-gray-500">Dein persönliches Orte-Tagebuch</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPlaceSelector(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Neuer Eintrag</span>
          </button>
          <button
            onClick={loadNotes}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            title="Aktualisieren"
          >
            <RefreshCw className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Place Selector Modal */}
      {showPlaceSelector && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-lg">
            <PlaceSelector
              places={places}
              recentNotes={notes}
              onSelect={handlePlaceSelect}
              onCancel={() => setShowPlaceSelector(false)}
            />
          </div>
        </div>
      )}

      {/* Create New Entry Modal */}
      {creatingForPlace && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-lg">
            <NoteEditor
              placeId={creatingForPlace.id}
              placeName={creatingForPlace.name}
              placeCategory={creatingForPlace.category as MainCategory | undefined}
              userId={userId}
              onSave={handleSaveNewEntry}
              onCancel={() => setCreatingForPlace(null)}
              isLoading={isSubmitting}
            />
          </div>
        </div>
      )}

      {/* View Toggle */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
        <button
          onClick={() => { setViewMode('timeline'); setSelectedDate(null); }}
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            viewMode === 'timeline'
              ? 'bg-white text-indigo-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Clock className="w-4 h-4" />
          <span className="hidden sm:inline">Timeline</span>
        </button>
        <button
          onClick={() => { setViewMode('calendar'); }}
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            viewMode === 'calendar'
              ? 'bg-white text-indigo-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <CalendarDays className="w-4 h-4" />
          <span className="hidden sm:inline">Kalender</span>
        </button>
        <button
          onClick={() => { setViewMode('list'); setSelectedDate(null); }}
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            viewMode === 'list'
              ? 'bg-white text-indigo-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <List className="w-4 h-4" />
          <span className="hidden sm:inline">Liste</span>
        </button>
      </div>

      {/* Memories - "An diesem Tag" */}
      {hasMemories && viewMode !== 'calendar' && (
        <LogbookMemories
          notes={notes}
          onNavigateToPlace={onNavigateToPlace}
          onViewNote={(note) => setEditingNote(note)}
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalNotes}</p>
              <p className="text-sm text-gray-500">Notizen</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <MapPin className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.uniquePlaces}</p>
              <p className="text-sm text-gray-500">Besuchte Orte</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mood Trends - Show when we have at least 3 notes */}
      {notes.length >= 3 && viewMode !== 'calendar' && (
        <MoodTrends notes={notes} />
      )}

      {/* Edit Modal */}
      {editingNote && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-lg">
            <NoteEditor
              placeId={editingNote.placeId}
              placeName={editingNote.placeName}
              placeCategory={editingNote.placeCategory}
              userId={userId}
              existingNote={editingNote}
              onSave={handleSaveEdit}
              onCancel={() => setEditingNote(null)}
              isLoading={isSubmitting}
            />
          </div>
        </div>
      )}

      {notes.length === 0 ? (
        <EmptyState
          type="notes"
          title="Noch keine Notizen"
          description="Besuche einen Ort und schreibe deine erste Notiz, um dein Logbook zu starten."
        />
      ) : (
        <>
          {/* Calendar View */}
          {viewMode === 'calendar' && (
            <LogbookCalendar
              notes={notes}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
            />
          )}

          {/* Search and Filter - only for list view */}
          {viewMode === 'list' && (
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Notizen durchsuchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-indigo-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-400" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-indigo-500"
                >
                  <option value="newest">Neueste zuerst</option>
                  <option value="oldest">Älteste zuerst</option>
                  <option value="rating">Nach Bewertung</option>
                </select>
              </div>
            </div>
          )}

          {/* Selected Date Header (for calendar view) */}
          {viewMode === 'calendar' && selectedDate && (
            <div className="flex items-center justify-between bg-indigo-50 rounded-lg px-4 py-2">
              <span className="text-sm font-medium text-indigo-800">
                Einträge für {selectedDate.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
              </span>
              <button
                onClick={() => setSelectedDate(null)}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                Alle anzeigen
              </button>
            </div>
          )}

          {/* Timeline View */}
          {viewMode === 'timeline' && (
            <LogbookTimeline
              notes={filteredNotes}
              onEdit={(note) => setEditingNote(note)}
              onDelete={(noteId) => handleDelete(noteId)}
              onNavigateToPlace={onNavigateToPlace}
            />
          )}

          {/* List View */}
          {(viewMode === 'list' || (viewMode === 'calendar' && selectedDate)) && (
            <>
              {filteredNotes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchQuery
                    ? `Keine Notizen gefunden für "${searchQuery}"`
                    : selectedDate
                      ? 'Keine Einträge für dieses Datum'
                      : 'Keine Notizen vorhanden'
                  }
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredNotes.map((note) => (
                    <NoteCard
                      key={note.id}
                      note={note}
                      onEdit={() => setEditingNote(note)}
                      onDelete={() => handleDelete(note.id)}
                      onNavigateToPlace={onNavigateToPlace}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default Logbook;
